import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  Pressable,
  Linking,
  StyleProp,
  ViewStyle,
} from 'react-native';
import {
  Colors,
  FontSize,
  FontWeight,
  Radii,
  Space,
  FontFamily,
} from '../theme/tokens';
import { Chip } from './Chip';
import type { ApiTicket } from '../schema/tickets/ticket.schema';
import {
  readHqEvidence,
  safeUrl,
  humanRule,
  type HqEvidence,
} from '../lib/hq-evidence';

// Re-exported so call sites can keep importing from the component. The
// defensive reading lives in lib/hq-evidence.ts, free of react-native.
export { readHqEvidence };
export type { HqEvidence };

/**
 * SKTICK-DB-001 Surface B — "Why this was raised".
 *
 * Rendered above the description when a ticket came from SK HQ Console. The
 * evidence rides in `customFields.hq*`, which is UNVALIDATED BY DESIGN — HQ
 * owns the shape. So every field here is read defensively: a missing key omits
 * its row, it never blanks the panel.
 *
 * Two rules that are not styling preferences:
 *
 *   The deep link is the PRIMARY ACTION. The fix happens in the target
 *   product, never here. An assignment-only outlet manager should be one tap
 *   from the place the work is actually done.
 *
 *   `hqExposure` is a DISPLAY STRING, not money. It is never summed, never
 *   totalled across rows and never charted. No money column exists outside SK
 *   Billing, and treating this as a number would quietly create one.
 */

/**
 * Chip tone per severity — a display concern, so it stays with the component
 * while the reading of the payload lives in lib/hq-evidence.ts.
 */
const SEVERITY_TONE: Record<string, 'danger' | 'warn' | 'neutral'> = {
  'Fix today': 'danger',
  'This week': 'warn',
  'Keep watching': 'neutral',
};

interface Props {
  ticket: Pick<ApiTicket, 'customFields'>;
  /** Product the deep link points at, for the action label. */
  targetProduct?: string;
  style?: StyleProp<ViewStyle>;
}

export function HqEvidencePanel({ ticket, targetProduct, style }: Props) {
  const e = readHqEvidence(ticket.customFields);
  const link = safeUrl(e.targetDeepLink);

  // Degrade, never blank. With most keys missing the panel still carries
  // whatever HQ did send.
  const hasAnything =
    e.ruleCode ||
    e.severityLabel ||
    e.exposure ||
    e.corroboration.length > 0 ||
    e.precedentCount ||
    e.period ||
    e.detectedAt ||
    link;
  if (!hasAnything) {
    return null;
  }

  return (
    <View style={[styles.panel, style]}>
      <View style={styles.header}>
        <Text style={styles.heading}>Why this was raised</Text>
        {e.severityLabel && (
          <Chip
            label={e.severityLabel}
            tone={SEVERITY_TONE[e.severityLabel] ?? 'neutral'}
          />
        )}
      </View>

      {e.ruleCode && (
        <Text style={styles.rule}>
          {humanRule(e.ruleCode)}
          {e.ruleVersion ? (
            <Text style={styles.ruleVersion}>{`  ·  v${e.ruleVersion}`}</Text>
          ) : null}
        </Text>
      )}

      {e.exposure && (
        <View style={styles.row}>
          <Text style={styles.label}>Exposure</Text>
          {/* Monospace and LTR. A display string — never summed, never charted. */}
          <Text style={styles.exposure}>
            {e.exposureCurrency
              ? `${e.exposureCurrency} ${e.exposure}`
              : e.exposure}
          </Text>
        </View>
      )}

      {e.corroboration.length > 0 && (
        <View style={styles.block}>
          <Text style={styles.label}>Also seen</Text>
          <View style={styles.chipWrap}>
            {e.corroboration.map(sig => (
              <Chip key={sig} label={humanRule(sig)} tone="default" />
            ))}
          </View>
        </View>
      )}

      {e.precedentCount && Number(e.precedentCount) > 0 && (
        <Text style={styles.precedent}>
          {`Happened ${e.precedentCount} ${
            Number(e.precedentCount) === 1 ? 'time' : 'times'
          } before`}
        </Text>
      )}

      {(e.period || e.detectedAt) && (
        <View style={styles.row}>
          {e.period ? (
            <Text style={styles.meta}>{`Period ${e.period}`}</Text>
          ) : (
            <View />
          )}
          {e.detectedAt ? (
            <Text style={styles.meta}>{`Detected ${new Date(
              e.detectedAt,
            ).toLocaleString()}`}</Text>
          ) : null}
        </View>
      )}

      {link && (
        // Primary action. The work is done in the target product, not here.
        <Pressable
          style={styles.action}
          accessibilityRole="link"
          onPress={() => {
            Linking.openURL(link).catch(() => {});
          }}
        >
          <Text style={styles.actionText}>
            {`Open in ${targetProduct ?? 'the target product'}`}
          </Text>
        </Pressable>
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: Space[3],
    padding: Space[4],
    backgroundColor: Colors.internalSoft,
    borderRadius: Radii.r3,
    borderWidth: 1,
    borderColor: Colors.internalLine,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Space[3],
  },
  heading: {
    fontSize: FontSize.md,
    fontWeight: FontWeight.semibold,
    color: Colors.internalInk,
    fontFamily: FontFamily.sans,
  },
  rule: {
    fontSize: FontSize.sm,
    color: Colors.ink2,
    fontFamily: FontFamily.sans,
  },
  ruleVersion: {
    color: Colors.ink3,
    fontFamily: FontFamily.mono,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: Space[3],
    flexWrap: 'wrap',
  },
  block: { gap: Space[2] },
  chipWrap: { flexDirection: 'row', flexWrap: 'wrap', gap: Space[2] },
  label: {
    fontSize: FontSize.sm,
    color: Colors.ink3,
    fontFamily: FontFamily.sans,
  },
  exposure: {
    fontSize: FontSize.lg,
    color: Colors.ink,
    fontFamily: FontFamily.mono,
    writingDirection: 'ltr',
  },
  precedent: {
    fontSize: FontSize.sm,
    color: Colors.ink2,
    fontFamily: FontFamily.sans,
  },
  meta: {
    fontSize: FontSize.xs,
    color: Colors.ink3,
    fontFamily: FontFamily.sans,
  },
  action: {
    alignSelf: 'flex-start',
    paddingVertical: Space[2],
    paddingHorizontal: Space[4],
    borderRadius: Radii.r2,
    backgroundColor: Colors.internal,
  },
  actionText: {
    fontSize: FontSize.md,
    fontWeight: FontWeight.semibold,
    color: Colors.white,
    fontFamily: FontFamily.sans,
  },
});
